import { useState } from 'react';
import { useForm } from '@tanstack/react-form';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useActionBar } from '@/lib/context/ActionBarContext';
import { useAuth } from '@/lib/hooks/useAuth';
import { useBackNavigation } from '@/lib/hooks/useBackNavigation';
import { useUserProfile } from '@/lib/hooks/useUserProfile';
import { useToastNotification } from '@/lib/hooks/useToastNotification';
import { guestFormSchema } from '@/lib/validators/guest-form.validator';
import { createGuest } from '@/lib/services/guests';
import { guestKeys } from './useGuests';
import type { GuestFormValues } from '@/lib/types/guests.types';
import type { GuestInsert } from '@/lib/types/database';

/**
 * Default values for the guest form
 */
const guestFormDefaultValues: GuestFormValues = {
  firstName: '',
  lastName: '',
  email: '',
  visitDate: '',
  reasonForVisit: '',
  acknowledgment: false,
};

/**
 * Convert GuestFormValues to GuestInsert format
 */
function formValuesToGuestInsert(
  values: GuestFormValues,
  registeredBy: string,
  registeredByName: string
): GuestInsert {
  // Convert visitDate to ISO string if provided
  const visitDate = values.visitDate
    ? new Date(`${values.visitDate}T00:00:00`).toISOString()
    : new Date().toISOString();

  return {
    registered_by: registeredBy,
    registered_by_name: registeredByName,
    first_name: values.firstName.trim(),
    last_name: values.lastName.trim(),
    email: values.email.trim(),
    visit_date: visitDate,
    reason_for_visit: values.reasonForVisit.trim(),
  };
}

/**
 * Type for the guest form instance returned by useGuestForm
 */
export type GuestFormInstance = ReturnType<typeof useGuestForm>['form'];

/**
 * Custom hook for managing guest form state and actions
 */
export function useGuestForm() {
  const handleBack = useBackNavigation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { profile } = useUserProfile();
  const { toast, showToast, hideToast } = useToastNotification();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Track whether form submission has been attempted (to show all errors)
  const [submitAttempted, setSubmitAttempted] = useState(false);
  // Store validation errors from Zod
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string[]>
  >({});

  const form = useForm({
    defaultValues: guestFormDefaultValues,
    onSubmit: async ({ value }: { value: GuestFormValues }) => {
      // Mark that submission was attempted
      setSubmitAttempted(true);

      // Validate form before submitting
      const validationResult = guestFormSchema.safeParse(value);

      if (!validationResult.success) {
        const flattened = validationResult.error.flatten();
        // Store the field errors so components can display them
        setValidationErrors(flattened.fieldErrors as Record<string, string[]>);
        return;
      }

      // Validation passed - clear errors and submit
      setValidationErrors({});
      setIsSubmitting(true);

      try {
        if (!user?.id) {
          throw new Error('User must be authenticated to register a guest');
        }

        const registeredByName =
          profile?.full_name || profile?.email || user.email || 'Unknown';

        const guestInsert = formValuesToGuestInsert(
          value,
          user.id,
          registeredByName
        );
        await createGuest(guestInsert);

        // Invalidate all guest queries so list and count refresh
        await queryClient.invalidateQueries({ queryKey: guestKeys.all });

        // Success - show success state
        setIsSuccess(true);
        setIsSubmitting(false);
        // Show success toast
        showToast({
          title: 'Guest registered',
          description: `${value.firstName} ${value.lastName} has been registered successfully.`,
          variant: 'success',
        });
      } catch (error) {
        console.error('Failed to register guest:', error);
        setIsSubmitting(false);
        // Show error toast
        showToast({
          title: 'Registration failed',
          description:
            error instanceof Error
              ? error.message
              : 'Failed to register guest. Please try again.',
          variant: 'error',
        });
      }
    },
  });

  // Handle form submission (called from ActionBar button)
  const handleSubmit = () => {
    void form.handleSubmit();
  };

  // Handle navigation to home after success
  const handleDone = () => {
    navigate({ to: '/' });
  };

  useActionBar({
    title: 'Register Guest',
    visible: !isSuccess,
    actions: [
      {
        key: 'cancel',
        label: 'Cancel',
        variant: 'secondary',
        fullWidth: true,
        onClick: handleBack,
      },
      {
        key: 'submit',
        label: 'Register Guest',
        variant: 'default',
        fullWidth: true,
        loading: isSubmitting,
        onClick: handleSubmit,
      },
    ],
    buttonStyle: undefined,
    subtext: 'Guest information will only be visible to admins',
  });

  return {
    form,
    isSubmitting,
    isSuccess,
    submitAttempted,
    validationErrors,
    handleDone,
    toast,
    hideToast,
  };
}
